import { useState } from "react";
import { cx } from "@/utils/cx";

const FALLBACK_SRC = "/empty-states/portrait-fallback.svg";

// Position, size and opacity of the portrait for most pages. A page that needs a
// different placement passes its own `className`, which replaces this wholesale.
const DEFAULT_POSITION = "-right-14 bottom-0 h-[105%] w-auto opacity-70";

type EmptyStatePortraitProps = {
  src: string;
  /** Where the left-hand fade reaches full opacity, e.g. "38%". */
  maskLeft?: string;
  /** Where the bottom fade begins, e.g. "80%". `null` drops the bottom fade entirely. */
  maskBottom?: string | null;
  /** Replaces the default position/size/opacity classes. */
  className?: string;
};

/**
 * Page portrait that bleeds off the right edge behind the content. It is masked
 * twice — fading in from the left and out towards the bottom — so it never
 * collides with the text, and it is decorative to assistive tech. If the
 * page-specific image fails to load it falls back to the generic halftone
 * portrait.
 */
export function EmptyStatePortrait({ src, maskLeft = "38%", maskBottom = "80%", className }: EmptyStatePortraitProps) {
  const [useFallback, setUseFallback] = useState(false);
  const leftMask = `linear-gradient(to right, transparent 0%, black ${maskLeft})`;
  const mask = maskBottom ? `${leftMask}, linear-gradient(black ${maskBottom}, transparent 100%)` : leftMask;

  return (
    <img
      aria-hidden="true"
      alt=""
      src={useFallback ? FALLBACK_SRC : src}
      onError={() => {
        if (!useFallback) setUseFallback(true);
      }}
      className={cx("pointer-events-none absolute -z-10 max-w-none", className ?? DEFAULT_POSITION)}
      style={{
        maskImage: mask,
        WebkitMaskImage: mask,
        maskComposite: "intersect",
        WebkitMaskComposite: "source-in",
      }}
    />
  );
}
